/**
 * What a failed API call says, as text a page can print.
 *
 * The API answers a failure with `{ detail }`, and `detail` comes in three
 * shapes:
 *   - a sentence — most refusals;
 *   - an object `{ error, message, ... }` — typed refusals that some pages read
 *     fields from (plan not serviceable, price changed, quota used up);
 *   - a LIST of `{ type, loc, msg, input, ctx }` — a request that failed
 *     validation (HTTP 422).
 * Call sites all over the app print `detail` as it comes. An object or a list
 * handed to React is not text: it throws, and the top-level error boundary
 * replaces the whole app.
 *
 * So the list is turned into one sentence here, once, before any call site
 * sees it: attachErrorNormalizer() goes on every axios client the app makes.
 * The sentence names the fields from `loc` and gives the limit from `ctx`. It
 * never repeats `msg` (pydantic's wording names no field) or `input` (what the
 * person typed, a password included).
 *
 * Objects are left as they are — pages read their fields — so a call site that
 * can receive one prints errorText(err, fallback) instead of `detail`.
 *
 * The backend builds the same sentence for its own 422 answers
 * (backend/validation_errors.py). The two share one list of cases,
 * backend/tests/fixtures/validation_error_cases.json: change the wording or
 * the tables below in both places and in that file.
 *
 * Plain .mjs with no imports, so frontend/scripts/api-errors.test.js can load
 * it without a build.
 */

export const GENERIC_VALIDATION_TEXT = 'Please check the details and try again.';

// Field names that read badly word by word.
export const FIELD_LABELS = {
  family_member_id: 'Family member',
  prior7_days_in_india: 'Days in India across the last 7 years',
  user_whatsapp: 'WhatsApp number',
};

// Words inside a field name that are not written the way they are spelled.
export const WORDS = {
  agri: 'agricultural',
  ai: 'AI',
  aud: '(AUD)',
  ay: 'assessment year',
  ays: 'assessment years',
  bf: 'brought-forward',
  bp: 'BP',
  cg: 'capital gains',
  cm: '(cm)',
  cs: 'CS',
  ded: 'deduction',
  dob: 'date of birth',
  ed: 'ED',
  fcnr: 'FCNR',
  fema: 'FEMA',
  gp: 'GP',
  hba1c: 'HbA1c',
  hcm: 'HCM',
  hp: 'house property',
  id: 'ID',
  ids: 'IDs',
  india: 'India',
  inr: '(INR)',
  ip: 'IP',
  ist: '(IST)',
  kg: '(kg)',
  ltcg: 'LTCG',
  ltcl: 'LTCL',
  nr: 'NR',
  nre: 'NRE',
  nri: 'NRI',
  o2: 'O2',
  p2: 'P2',
  pan: 'PAN',
  pct: '(%)',
  pincode: 'PIN code',
  pm: 'PM',
  qty: 'quantity',
  sku: 'SKU',
  spo2: 'SpO2',
  sqft: '(sq ft)',
  stcg: 'STCG',
  stcl: 'STCL',
  tcs: 'TCS',
  tds: 'TDS',
  tin: 'TIN',
  trc: 'TRC',
  url: 'URL',
  usd: '(USD)',
  utm: 'UTM',
  vda: 'VDA',
  via: 'VI-A',
  whatsapp: 'WhatsApp',
  xiia: 'XIIA',
};

// Where a failed value came from. Only what a person fills in is named:
// a header, cookie or path segment is the app's doing, not theirs.
const NAMED_SOURCES = ['body', 'query'];
const UNNAMED_SOURCES = ['path', 'header', 'cookie'];

// Failures about the request as a whole. `extra_forbidden` carries a key the
// sender made up, so naming it would print the sender's own text.
const WHOLE_REQUEST_TYPES = ['json_invalid', 'extra_forbidden'];

// pydantic appends the member it tried to the path of a union field
// (quantity -> "int", then "str"). Those are types, not fields.
const UNION_MEMBERS = ['int', 'str', 'float', 'bool', 'bytes', 'list', 'dict', 'none', 'decimal'];

// Every field the API validates is lower snake_case. Anything else in a path
// is a class name, a type expression, or a key the sender chose.
const FIELD_NAME = /^[a-z][a-z0-9_]{0,59}$/;

const WHOLE_NUMBER_TYPES = ['int_parsing', 'int_type', 'int_from_float'];
const NUMBER_TYPES = ['float_parsing', 'float_type', 'finite_number', 'decimal_parsing', 'decimal_type'];
const DATE_TYPES = [
  'date_type', 'date_parsing', 'date_from_datetime_parsing', 'date_from_datetime_inexact',
  'datetime_type', 'datetime_parsing', 'datetime_object_invalid', 'datetime_from_date_parsing',
  'time_type', 'time_parsing',
];

const has = (table, key) => Object.prototype.hasOwnProperty.call(table, key);

function fieldName(loc) {
  if (!Array.isArray(loc) || loc.length === 0) return null;
  if (UNNAMED_SOURCES.includes(loc[0])) return null;
  const parts = NAMED_SOURCES.includes(loc[0]) ? loc.slice(1) : loc;
  // List positions are numbers; everything else on the path is a name.
  const names = parts.filter((part) => typeof part === 'string');
  while (names.length > 1) {
    const last = names[names.length - 1];
    if (FIELD_NAME.test(last) && !UNION_MEMBERS.includes(last)) break;
    names.pop();
  }
  const name = names.length ? names[names.length - 1] : null;
  return name && FIELD_NAME.test(name) ? name : null;
}

export function fieldLabel(name) {
  if (has(FIELD_LABELS, name)) return FIELD_LABELS[name];
  const text = String(name)
    .split('_')
    .filter(Boolean)
    .map((word) => {
      if (has(WORDS, word)) return WORDS[word];
      // Section and form codes: 80c -> 80C, 111a -> 111A, 24b -> 24B.
      if (/^[0-9]+[a-z]/.test(word)) return word.toUpperCase();
      return word;
    })
    .join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatLimit(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (!Number.isInteger(value)) return String(value);
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function reasonFor(item) {
  const ctx = item.ctx && typeof item.ctx === 'object' ? item.ctx : {};
  const limit = (key) => formatLimit(ctx[key]);
  const type = item.type;
  if (type === 'missing') return 'required';
  if (type === 'string_too_long') return limit('max_length') && `at most ${limit('max_length')} characters`;
  if (type === 'string_too_short') {
    if (!limit('min_length')) return null;
    return ctx.min_length <= 1 ? 'required' : `at least ${limit('min_length')} characters`;
  }
  if (type === 'too_long') return limit('max_length') && `at most ${limit('max_length')} items`;
  if (type === 'too_short') {
    if (!limit('min_length')) return null;
    return ctx.min_length <= 1 ? 'required' : `at least ${limit('min_length')} items`;
  }
  if (type === 'greater_than_equal') return limit('ge') && `must be ${limit('ge')} or more`;
  if (type === 'greater_than') return limit('gt') && `must be more than ${limit('gt')}`;
  if (type === 'less_than_equal') return limit('le') && `must be ${limit('le')} or less`;
  if (type === 'less_than') return limit('lt') && `must be less than ${limit('lt')}`;
  if (WHOLE_NUMBER_TYPES.includes(type)) return 'must be a whole number';
  if (NUMBER_TYPES.includes(type)) return 'must be a number';
  if (type === 'string_pattern_mismatch') return 'not in the expected format';
  if (DATE_TYPES.includes(type)) return 'not a valid date or time';
  return null;
}

/**
 * One sentence for a list of validation failures.
 * One field: its name and what is wrong. Several: their names.
 */
export function describeValidationErrors(items) {
  const found = [];
  for (const item of Array.isArray(items) ? items : []) {
    if (!item || typeof item !== 'object') continue;
    if (WHOLE_REQUEST_TYPES.includes(item.type)) continue;
    const name = fieldName(item.loc);
    if (!name) continue;
    const label = fieldLabel(name);
    if (found.some((entry) => entry.label === label)) continue;
    found.push({ label, reason: reasonFor(item) || null });
  }
  if (found.length === 0) return GENERIC_VALIDATION_TEXT;
  if (found.length === 1) {
    const { label, reason } = found[0];
    return reason ? `Please check ${label}: ${reason}.` : `Please check ${label}.`;
  }
  const labels = found.map((entry) => entry.label);
  const shown = labels.slice(0, 3);
  const more = labels.length - shown.length;
  const list = more > 0
    ? `${shown.join(', ')} and ${more} more`
    : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
  return `Please check: ${list}.`;
}

/**
 * Makes `detail` printable in a parsed error body: a list becomes its
 * sentence, and the list itself stays available as `validation_errors`.
 * A sentence or an object is left alone. Returns the same body.
 */
export function normalizeErrorBody(body) {
  if (!body || typeof body !== 'object' || !Array.isArray(body.detail)) return body;
  body.validation_errors = body.detail;
  body.detail = describeValidationErrors(body.detail);
  return body;
}

/**
 * The server's reason for a failed call as text, or `fallback`.
 * For call sites that can be answered with an object `detail`.
 */
export function errorText(err, fallback) {
  const detail = err && err.response && err.response.data ? err.response.data.detail : undefined;
  if (typeof detail === 'string') return detail || fallback;
  if (Array.isArray(detail)) return describeValidationErrors(detail);
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message) {
    return detail.message;
  }
  return fallback;
}

const attached = new WeakSet();

/**
 * Normalizes every failed response of an axios client (an instance, or axios
 * itself for bare axios.get/post calls) before its callers see it. The error
 * object stays the same one; only `response.data.detail` can change.
 */
export function attachErrorNormalizer(client) {
  if (!client || !client.interceptors || attached.has(client)) return client;
  attached.add(client);
  client.interceptors.response.use(null, (err) => {
    try {
      normalizeErrorBody(err && err.response ? err.response.data : null);
    } catch {
      // Never let the tidy-up replace the real failure.
    }
    return Promise.reject(err);
  });
  return client;
}
